// FILE: apps/admin/src/pages/Quests.tsx
// VERSION: 2.0.0
// START_MODULE_CONTRACT
//   PURPOSE: Admin Quests & Tasks management page with 2 horizontal tabs (Quest Catalog CRUD with XP + RUB rewards and Rich Completion History with player avatars and profile links).
//   SCOPE: Quests component.
//   DEPENDS: ../api.js, ../components/Icons.js, ../components/UserAvatar.js, ../toast.js
//   LINKS: M-ADMIN
//   ROLE: RUNTIME
//   MAP_MODE: EXPORTS
// END_MODULE_CONTRACT
//
// START_MODULE_MAP
//   Quests - Two-tab Admin Quests catalog CRUD editor and player completions monitor
// END_MODULE_MAP

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';

import { api, dt, num } from '../api.js';
import { AdminIcon } from '../components/Icons.js';
import { UserAvatar } from '../components/UserAvatar.js';
import { useToast } from '../toast.js';

interface QuestItem {
  id: string;
  key?: string;
  title: string;
  subtitle: string;
  kind?: string;
  goal: number;
  rewardXp: number;
  rewardRub?: number;
  active: boolean;
  completionsCount: number;
}

interface QuestCompletionRow {
  id: string;
  questId: string;
  questKey?: string | null;
  questTitle?: string;
  rewardXp?: number;
  rewardRub?: number;
  userId: string;
  telegramId?: number | null;
  username?: string | null;
  displayName?: string;
  photoUrl?: string | null;
  completedAt: string;
}

interface QuestsOverviewResponse {
  quests: QuestItem[];
  totalCompletions: number;
  recentCompletions: QuestCompletionRow[];
}

interface CreateQuestDraft {
  key: string;
  title: string;
  subtitle: string;
  kind: string;
  goal: string;
  rewardXp: string;
  rewardRub: string;
  active: boolean;
}

interface EditQuestDraft {
  id: string;
  key: string;
  title: string;
  subtitle: string;
  kind: string;
  goal: string;
  rewardXp: string;
  rewardRub: string;
  active: boolean;
}

type QuestsTab = 'catalog' | 'completions';

const QUEST_KIND_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'visit', label: 'Вход в приложение' },
  { value: 'orders_count', label: 'Количество выполненных заказов' },
  { value: 'stars_bought', label: 'Куплено Stars' },
  { value: 'referrals', label: 'Приглашено друзей' },
  { value: 'crash', label: 'Ставки в Краш' },
];

function describeQuestKind(kind: string | undefined): string {
  // START_BLOCK_DESCRIBE_QUEST_KIND
  if (!kind) return 'Вход в приложение';
  const match = QUEST_KIND_OPTIONS.find((opt) => opt.value === kind);
  if (match) return match.label;
  if (kind === 'cases') return 'Открытие кейсов';
  return kind;
  // END_BLOCK_DESCRIBE_QUEST_KIND
}

function fmtRubAmount(value: number): string {
  // START_BLOCK_FMT_RUB_AMOUNT
  return value.toLocaleString('ru-RU', {
    minimumFractionDigits: value % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  });
  // END_BLOCK_FMT_RUB_AMOUNT
}

function createInitialQuestDraft(): CreateQuestDraft {
  // START_BLOCK_CREATE_INITIAL_QUEST_DRAFT
  return {
    key: '',
    title: '',
    subtitle: '',
    kind: 'orders_count',
    goal: '1',
    rewardXp: '100',
    rewardRub: '0',
    active: true,
  };
  // END_BLOCK_CREATE_INITIAL_QUEST_DRAFT
}

// START_CONTRACT: Quests
//   PURPOSE: Render the 2-tab Quests management workspace (Quest Catalog with creation/editing of XP + RUB rewards and Completion History with user avatars).
//   INPUTS: none
//   OUTPUTS: React element
//   SIDE_EFFECTS: Calls GET /admin/quests, POST /admin/quests, and PUT /admin/quests/:id
//   LINKS: M-ADMIN
// END_CONTRACT: Quests
export function Quests() {
  // START_BLOCK_QUESTS
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();

  const [tab, setTab] = useState<QuestsTab>('catalog');
  const [creating, setCreating] = useState<CreateQuestDraft | null>(null);
  const [editing, setEditing] = useState<EditQuestDraft | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['admin-quests'],
    queryFn: () => api.get<QuestsOverviewResponse>('/admin/quests'),
  });

  const saveMut = useMutation({
    mutationFn: (payload: {
      id: string;
      title?: string;
      subtitle?: string;
      goal?: number;
      rewardXp?: number;
      rewardRub?: number;
      active?: boolean;
      reason?: string;
    }) => {
      const { id, ...body } = payload;
      return api.put(`/admin/quests/${encodeURIComponent(id)}`, body);
    },
    onSuccess: () => {
      toast('Параметры задания сохранены');
      setEditing(null);
      void qc.invalidateQueries({ queryKey: ['admin-quests'] });
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'Ошибка сохранения задания'),
  });

  const createMut = useMutation({
    mutationFn: (body: {
      key: string;
      title: string;
      subtitle: string;
      kind: string;
      goal: number;
      rewardXp: number;
      rewardRub: number;
      active: boolean;
    }) => api.post('/admin/quests', body),
    onSuccess: () => {
      toast('Новое задание создано');
      setCreating(null);
      void qc.invalidateQueries({ queryKey: ['admin-quests'] });
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'Ошибка создания задания'),
  });

  const handleSubmitEdit = () => {
    if (!editing) return;
    if (!editing.title.trim()) {
      return toast('Укажите название задания');
    }
    const goal = Math.round(Number(editing.goal.replace(',', '.')));
    const rewardXp = Math.round(Number(editing.rewardXp.replace(',', '.')));
    const rewardRub = Number(editing.rewardRub.replace(',', '.'));
    if (!Number.isFinite(goal) || goal < 1) {
      return toast('Целевое значение должно быть не меньше 1');
    }
    if (!Number.isFinite(rewardXp) || rewardXp < 0) {
      return toast('Награда XP не может быть отрицательной');
    }
    if (!Number.isFinite(rewardRub) || rewardRub < 0) {
      return toast('Награда в рублях не может быть отрицательной');
    }
    saveMut.mutate({
      id: editing.id,
      title: editing.title.trim(),
      subtitle: editing.subtitle.trim(),
      goal,
      rewardXp,
      rewardRub,
      active: editing.active,
      reason: `Обновление задания ${editing.key}`,
    });
  };

  const handleSubmitCreate = () => {
    if (!creating) return;
    const cleanKey = creating.key
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9_]/g, '_');
    if (!cleanKey) {
      return toast('Укажите код задания (латиница, цифры, подчёркивания)');
    }
    if (!creating.title.trim()) {
      return toast('Укажите название задания');
    }
    const goal = Math.round(Number(creating.goal.replace(',', '.')));
    const rewardXp = Math.round(Number(creating.rewardXp.replace(',', '.')));
    const rewardRub = Number(creating.rewardRub.replace(',', '.'));
    if (!Number.isFinite(goal) || goal < 1) {
      return toast('Целевое значение должно быть не меньше 1');
    }
    if (!Number.isFinite(rewardXp) || rewardXp < 0) {
      return toast('Награда XP не может быть отрицательной');
    }
    if (!Number.isFinite(rewardRub) || rewardRub < 0) {
      return toast('Награда в рублях не может быть отрицательной');
    }
    createMut.mutate({
      key: cleanKey,
      title: creating.title.trim(),
      subtitle: creating.subtitle.trim(),
      kind: creating.kind,
      goal,
      rewardXp,
      rewardRub,
      active: creating.active,
    });
  };

  const quests = data?.quests ?? [];
  const completions = data?.recentCompletions ?? [];

  const renderModal = (node: ReactNode) => {
    if (typeof document !== 'undefined') {
      return createPortal(node, document.body);
    }
    return node;
  };

  return (
    <>
      <div className="page-header">
        <div>
          <h1>Задания и квесты</h1>
          <div className="page-subtitle">
            Каталог игровых заданий Mini App с наградами в XP и рублях на баланс · Всего выполнений:{' '}
            <strong className="mono">{num(data?.totalCompletions ?? 0)}</strong>
          </div>
        </div>
        <button
          type="button"
          className="btn primary"
          onClick={() => setCreating(createInitialQuestDraft())}
        >
          <AdminIcon name="plus" size={14} />
          <span>Новое задание</span>
        </button>
      </div>

      <div className="tabs">
        <button
          type="button"
          className={`tab-btn ${tab === 'catalog' ? 'active' : ''}`}
          onClick={() => setTab('catalog')}
        >
          Каталог заданий ({quests.length})
        </button>
        <button
          type="button"
          className={`tab-btn ${tab === 'completions' ? 'active' : ''}`}
          onClick={() => setTab('completions')}
        >
          История выполнений ({num(data?.totalCompletions ?? 0)})
        </button>
      </div>

      {isLoading ? (
        <div className="muted">Загрузка раздела заданий…</div>
      ) : tab === 'catalog' ? (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Код (key)</th>
                  <th>Название и описание</th>
                  <th>Тип проверки</th>
                  <th>Цель</th>
                  <th>Награда</th>
                  <th>Выполнений</th>
                  <th>Статус</th>
                  <th>Действие</th>
                </tr>
              </thead>
              <tbody>
                {quests.map((q) => {
                  const questKey = q.key ?? q.id;
                  const rubVal = q.rewardRub ?? 0;
                  return (
                    <tr key={q.id}>
                      <td className="mono" style={{ fontWeight: 600 }}>
                        {questKey}
                      </td>
                      <td>
                        <div style={{ fontWeight: 600 }}>{q.title}</div>
                        {q.subtitle && (
                          <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
                            {q.subtitle}
                          </div>
                        )}
                      </td>
                      <td>
                        <span className="chip info">{describeQuestKind(q.kind)}</span>
                      </td>
                      <td className="mono">{num(q.goal)}</td>
                      <td className="mono">
                        <div style={{ fontWeight: 600 }}>+{num(q.rewardXp)} XP</div>
                        {rubVal > 0 && (
                          <div
                            style={{
                              fontSize: 12,
                              fontWeight: 600,
                              color: 'var(--green)',
                              marginTop: 2,
                            }}
                          >
                            +{fmtRubAmount(rubVal)} ₽
                          </div>
                        )}
                      </td>
                      <td className="mono">{num(q.completionsCount)}</td>
                      <td>
                        <span className={`chip ${q.active ? 'ok' : 'CANCELLED'}`}>
                          {q.active ? 'В МИНИ-АППЕ' : 'СКРЫТ'}
                        </span>
                      </td>
                      <td>
                        <div className="row" style={{ gap: 6, flexWrap: 'nowrap' }}>
                          <button
                            type="button"
                            className="btn sm primary"
                            onClick={() =>
                              setEditing({
                                id: q.id,
                                key: questKey,
                                title: q.title,
                                subtitle: q.subtitle ?? '',
                                kind: q.kind ?? 'visit',
                                goal: String(q.goal),
                                rewardXp: String(q.rewardXp),
                                rewardRub: String(q.rewardRub ?? 0),
                                active: q.active,
                              })
                            }
                          >
                            Изменить
                          </button>
                          <button
                            type="button"
                            className="btn sm ghost"
                            disabled={saveMut.isPending}
                            onClick={() =>
                              saveMut.mutate({
                                id: q.id,
                                active: !q.active,
                                reason: `${!q.active ? 'Включение' : 'Скрытие'} задания ${questKey}`,
                              })
                            }
                          >
                            {q.active ? 'Скрыть' : 'Показать'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {quests.length === 0 && (
                  <tr>
                    <td colSpan={8} className="muted" style={{ textAlign: 'center', padding: 28 }}>
                      В каталоге пока нет заданий. Нажмите «Новое задание», чтобы добавить первое условие.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Задание</th>
                  <th>Награда</th>
                  <th>Кто выполнил</th>
                  <th>Дата и время</th>
                </tr>
              </thead>
              <tbody>
                {completions.map((c) => {
                  const questTitle = c.questTitle || c.questKey || c.questId;
                  const questBadge = c.questKey ?? c.questId;
                  const xpVal = c.rewardXp ?? 0;
                  const rubVal = c.rewardRub ?? 0;
                  const playerName =
                    c.displayName?.trim() ||
                    (c.username ? `@${c.username}` : `TG #${c.telegramId ?? c.userId.slice(0, 8)}`);
                  const playerSubParts: string[] = [];
                  if (c.username && playerName !== `@${c.username}`) {
                    playerSubParts.push(`@${c.username}`);
                  }
                  if (c.telegramId) {
                    playerSubParts.push(`TG #${c.telegramId}`);
                  }
                  const userQuery = String(c.telegramId ?? c.username ?? c.userId);

                  return (
                    <tr key={c.id}>
                      <td>
                        <div style={{ fontWeight: 600 }}>{questTitle}</div>
                        <div style={{ marginTop: 3 }}>
                          <span
                            className="mono muted"
                            style={{
                              fontSize: 11,
                              padding: '1px 6px',
                              borderRadius: 5,
                              background: 'var(--bg-2)',
                              border: '1px solid var(--hair)',
                            }}
                          >
                            {questBadge}
                          </span>
                        </div>
                      </td>
                      <td className="mono">
                        <div style={{ fontWeight: 600 }}>+{num(xpVal)} XP</div>
                        {rubVal > 0 && (
                          <div
                            style={{
                              fontSize: 12,
                              fontWeight: 600,
                              color: 'var(--green)',
                              marginTop: 2,
                            }}
                          >
                            +{fmtRubAmount(rubVal)} ₽
                          </div>
                        )}
                      </td>
                      <td>
                        <button
                          type="button"
                          onClick={() =>
                            navigate(
                              `/users?q=${encodeURIComponent(userQuery)}${c.userId ? `&userId=${encodeURIComponent(c.userId)}` : ''}`,
                            )
                          }
                          title={`Открыть профиль ${playerName}`}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 10,
                            padding: '3px 8px 3px 3px',
                            borderRadius: 9,
                            background: 'transparent',
                            border: '1px solid transparent',
                            color: 'inherit',
                            textAlign: 'left',
                            cursor: 'pointer',
                          }}
                        >
                          <UserAvatar
                            photoUrl={c.photoUrl}
                            name={playerName}
                            size={30}
                          />
                          <span style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <span style={{ fontWeight: 600, fontSize: 13.5, lineHeight: 1.2 }}>
                              {playerName}
                            </span>
                            <span
                              className="mono muted"
                              style={{ fontSize: 11.5, lineHeight: 1.2 }}
                            >
                              {playerSubParts.length > 0
                                ? playerSubParts.join(' · ')
                                : c.userId.slice(0, 8)}
                            </span>
                          </span>
                        </button>
                      </td>
                      <td className="muted mono">{dt(c.completedAt)}</td>
                    </tr>
                  );
                })}
                {completions.length === 0 && (
                  <tr>
                    <td colSpan={4} className="muted" style={{ textAlign: 'center', padding: 32 }}>
                      Выполнений заданий пока нет — записи появятся здесь сразу после того, как игроки заберут награду в Mini App.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {creating &&
        renderModal(
          <div className="modal-backdrop" onClick={() => setCreating(null)}>
            <div
              className="modal-card"
              style={{ maxWidth: 520 }}
              onClick={(e) => e.stopPropagation()}
            >
              <h2 style={{ marginTop: 0 }}>Новое задание для Mini App</h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div className="resp-grid-2">
                  <div>
                    <label className="field-label">Код задания (key)</label>
                    <input
                      className="mono"
                      style={{ width: '100%' }}
                      placeholder="buy_500_stars"
                      value={creating.key}
                      onChange={(e) => setCreating({ ...creating, key: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="field-label">Тип проверки</label>
                    <select
                      style={{ width: '100%', minWidth: 0 }}
                      value={creating.kind}
                      onChange={(e) => setCreating({ ...creating, kind: e.target.value })}
                    >
                      {QUEST_KIND_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="field-label">Название в Mini App</label>
                  <input
                    style={{ width: '100%' }}
                    placeholder="Например: Морской коллекционер"
                    value={creating.title}
                    onChange={(e) => setCreating({ ...creating, title: e.target.value })}
                  />
                </div>

                <div>
                  <label className="field-label">Краткое описание условий</label>
                  <input
                    style={{ width: '100%' }}
                    placeholder="Купите суммарно 500 Telegram Stars"
                    value={creating.subtitle}
                    onChange={(e) => setCreating({ ...creating, subtitle: e.target.value })}
                  />
                </div>

                <div className="resp-grid-3">
                  <div>
                    <label className="field-label">Цель (Goal)</label>
                    <input
                      type="number"
                      min="1"
                      className="mono"
                      style={{ width: '100%' }}
                      value={creating.goal}
                      onChange={(e) => setCreating({ ...creating, goal: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="field-label">Награда, XP</label>
                    <input
                      type="number"
                      min="0"
                      step="10"
                      className="mono"
                      style={{ width: '100%' }}
                      value={creating.rewardXp}
                      onChange={(e) => setCreating({ ...creating, rewardXp: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="field-label">Награда на баланс, ₽</label>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      className="mono"
                      style={{ width: '100%' }}
                      value={creating.rewardRub}
                      onChange={(e) => setCreating({ ...creating, rewardRub: e.target.value })}
                    />
                  </div>
                </div>

                <label className="row" style={{ gap: 8, cursor: 'pointer', marginTop: 4 }}>
                  <input
                    type="checkbox"
                    checked={creating.active}
                    onChange={(e) => setCreating({ ...creating, active: e.target.checked })}
                  />
                  <span>Сразу показывать задание игрокам в Mini App</span>
                </label>
              </div>

              <div className="row" style={{ justifyContent: 'flex-end', marginTop: 18 }}>
                <button type="button" className="btn ghost" onClick={() => setCreating(null)}>
                  Отмена
                </button>
                <button
                  type="button"
                  className="btn primary"
                  disabled={createMut.isPending}
                  onClick={handleSubmitCreate}
                >
                  <AdminIcon name="check" size={13} />
                  <span>{createMut.isPending ? 'Создание…' : 'Создать задание'}</span>
                </button>
              </div>
            </div>
          </div>,
        )}

      {editing &&
        renderModal(
          <div className="modal-backdrop" onClick={() => setEditing(null)}>
            <div
              className="modal-card"
              style={{ maxWidth: 520 }}
              onClick={(e) => e.stopPropagation()}
            >
              <h2 style={{ marginTop: 0 }}>
                Редактирование задания: <span className="mono">{editing.key}</span>
              </h2>
              <div className="muted" style={{ fontSize: 12.5, marginBottom: 12 }}>
                Тип проверки: <strong>{describeQuestKind(editing.kind)}</strong>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div>
                  <label className="field-label">Заголовок</label>
                  <input
                    style={{ width: '100%' }}
                    value={editing.title}
                    onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                  />
                </div>
                <div>
                  <label className="field-label">Подзаголовок</label>
                  <input
                    style={{ width: '100%' }}
                    value={editing.subtitle}
                    onChange={(e) => setEditing({ ...editing, subtitle: e.target.value })}
                  />
                </div>
                <div className="resp-grid-3">
                  <div>
                    <label className="field-label">Цель (Goal)</label>
                    <input
                      type="number"
                      min="1"
                      className="mono"
                      style={{ width: '100%' }}
                      value={editing.goal}
                      onChange={(e) => setEditing({ ...editing, goal: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="field-label">Награда, XP</label>
                    <input
                      type="number"
                      min="0"
                      step="10"
                      className="mono"
                      style={{ width: '100%' }}
                      value={editing.rewardXp}
                      onChange={(e) => setEditing({ ...editing, rewardXp: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="field-label">Награда на баланс, ₽</label>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      className="mono"
                      style={{ width: '100%' }}
                      value={editing.rewardRub}
                      onChange={(e) => setEditing({ ...editing, rewardRub: e.target.value })}
                    />
                  </div>
                </div>
                <label className="row" style={{ gap: 8, cursor: 'pointer', marginTop: 4 }}>
                  <input
                    type="checkbox"
                    checked={editing.active}
                    onChange={(e) => setEditing({ ...editing, active: e.target.checked })}
                  />
                  <span>Задание активно и отображается игрокам в Mini App</span>
                </label>
              </div>
              <div className="row" style={{ justifyContent: 'flex-end', marginTop: 18 }}>
                <button type="button" className="btn ghost" onClick={() => setEditing(null)}>
                  Отмена
                </button>
                <button
                  type="button"
                  className="btn primary"
                  disabled={saveMut.isPending}
                  onClick={handleSubmitEdit}
                >
                  <AdminIcon name="check" size={13} />
                  <span>{saveMut.isPending ? 'Сохранение…' : 'Сохранить'}</span>
                </button>
              </div>
            </div>
          </div>,
        )}
    </>
  );
  // END_BLOCK_QUESTS
}
